import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { apiFetch } from '../../services/api';

/**
 * TriggerRunForm: form to start a new workflow execution.
 *
 * Derived from workflow.canvasDefinition.inputParameters schema.
 * Currently simplified to accept JSON input directly.
 *
 * Props:
 *   workflowId: UUID
 *   canvasDefinition: { inputParameters?: [...] }
 *   onExecutionStarted: callback(runId)
 */
export const TriggerRunForm = ({ workflowId, canvasDefinition, onExecutionStarted }) => {
  const [inputJson, setInputJson] = useState('{}');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let parsedInput;
      try {
        parsedInput = JSON.parse(inputJson);
      } catch {
        throw new Error('Invalid JSON in input');
      }

      // Canonical start-execution endpoint on the unified WorkflowController
      // surface — see refactor #36. Routed through the shared apiFetch so the
      // mutating POST carries auth + the CSRF double-submit header + cookie and
      // the single-flight token refresh (no hand-rolled / dead-localStorage
      // bearer). apiFetch throws on non-OK with the server error message.
      const res = await apiFetch(`/api/v1/workflows/${workflowId}/execute`, 'POST', { input: parsedInput });
      const result = res.data || {};
      setInputJson('{}');
      if (onExecutionStarted) {
        onExecutionStarted(result.runId);
      }
    } catch (err) {
      console.error('Failed to start execution:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Workflow Input (JSON)
        </label>
        <textarea
          value={inputJson}
          onChange={(e) => setInputJson(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
          rows={10}
          placeholder='{"param1": "value1"}'
        />
        <p className="text-xs text-gray-600 mt-1">
          Enter workflow input as JSON. Leave empty {} for workflows without input parameters.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium px-4 py-2 rounded transition"
      >
        <Play size={18} />
        {loading ? 'Starting...' : 'Start Execution'}
      </button>
    </form>
  );
};
